import React, { useState, useEffect } from 'react';
import { Download, Printer, Building2, Search, Wallet, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { exportToPDF } from '@/utils/groupReportPDF';

const GroupSalaryReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0, 8) + '01');
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedCollege, setSelectedCollege] = useState('');
  const [colleges, setColleges] = useState([]);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchColleges(); fetchReport(); }, []);

  const fetchColleges = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/groups/my-group`, { headers: { Authorization: `Bearer ${token}` } });
      setColleges(res.data.colleges || []);
    } catch (e) { console.error(e); }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/groups/my-group/reports/salary?from_date=${fromDate}&to_date=${toDate}`;
      if (selectedCollege) url += `&college_id=${selectedCollege}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  const exportToExcel = () => {
    if (!data?.all_salaries?.length) return toast.error('No data to export');
    const ws = XLSX.utils.json_to_sheet(data.all_salaries.map(s => ({
      'College': s.college_name, 'Staff': s.staff_name, 'Designation': s.designation,
      'Month': s.month, 'Basic Salary': s.basic_salary, 'Net Amount': s.amount, 'Mode': s.payment_mode, 'Date': s.date
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Salary Report');
    XLSX.writeFile(wb, `Salary_Report_${fromDate}_${toDate}.xlsx`);
  };

  const exportPDF = () => {
    if (!data?.all_salaries?.length) return toast.error('No data to export');
    exportToPDF('Salary Report',
      ['College', 'Staff', 'Designation', 'Month', 'Amount', 'Mode', 'Date'],
      data.all_salaries.map(s => [s.college_name, s.staff_name, s.designation, s.month, `₹${(s.amount||0).toLocaleString('en-IN')}`, s.payment_mode, s.date]),
      `Salary_Report_${fromDate}_${toDate}`,
      `Period: ${fromDate} to ${toDate} | Total: ₹${(data.totals?.total_salary||0).toLocaleString('en-IN')}`
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="group-salary-report">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Salary Report</h1>
          <p className="text-sm text-gray-500">Staff salary payments across all colleges</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportToExcel} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"><Download className="h-4 w-4" /> Excel</button>
          <button onClick={exportPDF} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"><FileText className="h-4 w-4" /> PDF</button>
          <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"><Printer className="h-4 w-4" /> Print</button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-4 mb-6 flex flex-wrap gap-4 items-end">
        <div><label className="block text-xs font-medium text-gray-600 mb-1">From</label><input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">To</label><input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">College</label>
          <select value={selectedCollege} onChange={e => setSelectedCollege(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
            <option value="">All Colleges</option>{colleges.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <button onClick={fetchReport} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2"><Search className="h-4 w-4" /> Search</button>
      </div>

      {loading ? <div className="text-center py-12 text-gray-500">Loading...</div> : data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-purple-500">
              <p className="text-xs text-gray-500">Total Salary Paid</p>
              <p className="text-3xl font-bold text-purple-700">₹{(data.totals?.total_salary || 0).toLocaleString('en-IN')}</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-blue-500">
              <p className="text-xs text-gray-500">Staff Paid</p>
              <p className="text-3xl font-bold text-blue-700">{data.totals?.total_staff_paid || 0}</p>
            </div>
          </div>
          {data.colleges?.map(college => (
            <div key={college.college_id} className="bg-white rounded-xl shadow mb-4">
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-2"><Building2 className="h-5 w-5 text-blue-600" /><h3 className="font-bold">{college.college_name}</h3><span className="text-xs text-gray-500">({college.staff_paid} staff)</span></div>
                <span className="text-lg font-bold text-purple-700">₹{(college.total_salary || 0).toLocaleString('en-IN')}</span>
              </div>
              {college.salaries?.length > 0 ? (
                <div className="overflow-x-auto"><table className="w-full text-sm"><thead className="bg-gray-50"><tr>
                  <th className="text-left p-3">Staff Name</th><th className="text-left p-3">Designation</th><th className="text-left p-3">Month</th><th className="text-left p-3">Mode</th><th className="text-left p-3">Date</th><th className="text-right p-3">Amount</th>
                </tr></thead><tbody className="divide-y">{college.salaries.map((s, i) => (
                  <tr key={i} className="hover:bg-gray-50"><td className="p-3 font-medium">{s.staff_name}</td><td className="p-3">{s.designation}</td><td className="p-3">{s.month}</td><td className="p-3">{s.payment_mode}</td><td className="p-3">{s.date}</td><td className="p-3 text-right font-medium">₹{(s.amount || 0).toLocaleString('en-IN')}</td></tr>
                ))}</tbody></table></div>
              ) : <p className="p-4 text-gray-400 text-sm">No salary payments in this period</p>}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default GroupSalaryReport;
